import React, { useCallback, useMemo, useRef, useState, useEffect } from 'react';
import MaterialTable, { MTableToolbar } from 'material-table';
import { createTheme, MuiThemeProvider } from '@material-ui/core/styles';

const PROVIDER_LABELS = {
  google_oauth2: 'Google',
  facebook: 'Facebook',
  email: 'Email',
  halohermina_email: 'Hermina',
};

const PROVIDER_VARIANTS = {
  google_oauth2: 'google',
  facebook: 'facebook',
  email: 'email',
  halohermina_email: 'hermina',
};

const PLATFORM_LABELS = {
  web: 'Web',
  ios: 'iOS',
  android: 'Android',
  unknown: 'Unknown',
};

const PatientsDataTable = ({ translations = {} }) => {
  const tableRef = useRef(null);
  const [totalCount, setTotalCount] = useState(0);
  const [isDarkMode, setIsDarkMode] = useState(false);

  useEffect(() => {
    const checkDarkMode = () => {
      setIsDarkMode(document.documentElement.getAttribute('data-mode-theme') === 'dark');
    };

    checkDarkMode();

    const observer = new MutationObserver(checkDarkMode);
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['data-mode-theme'] });

    return () => observer.disconnect();
  }, []);

  const theme = useMemo(() => createTheme({
    palette: {
      type: isDarkMode ? 'dark' : 'light',
      primary: { main: '#2f8f63' },
      background: {
        paper: isDarkMode ? '#17212b' : '#ffffff',
        default: isDarkMode ? '#101923' : '#f8fafc',
      },
      text: {
        primary: isDarkMode ? '#e2e8f0' : '#0f172a',
        secondary: isDarkMode ? '#94a3b8' : '#64748b',
      },
    },
  }), [isDarkMode]);

  const tableStyles = useMemo(() => ({
    headerStyle: {
      backgroundColor: 'transparent',
      color: isDarkMode ? '#cbd5e1' : '#64748b',
      fontWeight: 700,
      fontSize: '0.74rem',
      textTransform: 'uppercase',
      letterSpacing: '0.08em',
      whiteSpace: 'nowrap',
      padding: '0.95rem 1.25rem',
      borderBottom: `1px solid ${isDarkMode ? 'rgba(71, 85, 105, 0.6)' : 'rgba(226, 232, 240, 0.95)'}`,
    },
    rowStyle: {
      backgroundColor: 'transparent',
      color: isDarkMode ? '#e2e8f0' : '#0f172a',
    },
    cellStyle: {
      padding: '1rem 1.25rem',
      fontSize: '0.92rem',
      borderBottom: `1px solid ${isDarkMode ? 'rgba(71, 85, 105, 0.45)' : 'rgba(226, 232, 240, 0.9)'}`,
    },
  }), [isDarkMode]);

  const formatNumber = useCallback((value) => {
    if (value === null || value === undefined) return '0';
    return new Intl.NumberFormat().format(value);
  }, []);

  const unknownLabel = translations.unknown || '-';

  const getValueOrFallback = useCallback((value) => {
    if (value === 0) return '0';
    if (value === null || value === undefined || value === '') return unknownLabel;
    return value;
  }, [unknownLabel]);

  const getPresentValue = useCallback((value) => {
    if (value === null || value === undefined) return null;
    if (typeof value === 'string') {
      const trimmedValue = value.trim();
      return trimmedValue === '' ? null : trimmedValue;
    }
    return value;
  }, []);

  const getInitials = useCallback((name) => {
    if (!name) return 'NA';

    return name
      .split(' ')
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part.charAt(0).toUpperCase())
      .join('');
  }, []);

  const getProviderLabel = useCallback((provider) => {
    if (!provider) return unknownLabel;
    return PROVIDER_LABELS[provider] || provider.replace(/_/g, ' ');
  }, [unknownLabel]);

  const getPlatformLabel = useCallback((platform) => {
    if (!platform) return unknownLabel;
    return PLATFORM_LABELS[platform] || platform.replace(/_/g, ' ');
  }, [unknownLabel]);

  const buildUrl = useCallback((query) => {
    const params = new URLSearchParams();

    params.set('per_page', query.pageSize);
    params.set('q', query.search || '');
    params.set('page', query.page + 1);

    if (query.orderBy?.field) {
      params.set('sort', query.orderBy.field);
      params.set('direction', query.orderDirection || 'asc');
    }

    return `/api/accounts?${params.toString()}`;
  }, []);

  const fetchData = useCallback((query) =>
    new Promise((resolve, reject) => {
      fetch(buildUrl(query))
        .then((response) => {
          if (!response.ok) throw new Error(`HTTP ${response.status}`);
          return response.json();
        })
        .then((result) => {
          setTotalCount(result.pagination?.count || 0);
          resolve({
            data: result.accounts?.data || [],
            page: (result.pagination?.page || 1) - 1,
            totalCount: result.pagination?.count || 0,
          });
        })
        .catch((error) => reject(error));
    }), [buildUrl]);

  const renderBadge = useCallback((value, variant = 'default') => {
    const variantClass = {
      default: 'patient-badge--default',
      primary: 'patient-badge--primary',
      male: 'patient-badge--male',
      female: 'patient-badge--female',
      google: 'patient-badge--google',
      facebook: 'patient-badge--facebook',
      email: 'patient-badge--email',
      hermina: 'patient-badge--hermina',
    }[variant] || 'patient-badge--default';

    return (
      <span className={`patient-badge ${variantClass}`}>{value}</span>
    );
  }, []);

  const renderPatientCell = useCallback((rowData) => {
    const { attributes } = rowData;
    const name = getValueOrFallback(attributes?.full_name);
    const email = getValueOrFallback(attributes?.email);
    const age = getValueOrFallback(attributes?.age);
    const contact = getPresentValue(attributes?.mobile_phone);
    const initials = getInitials(attributes?.full_name);
    const patientTitle = `${name} - ${translations.age || 'Age'} ${age}`;

    return (
      <div className="patient-cell">
        <div className="patient-avatar">
          {attributes?.avatar ? (
            <img src={attributes.avatar} alt={name} className="patient-avatar__image" />
          ) : (
            <span className="patient-avatar__initials">{initials}</span>
          )}
        </div>
        <div className="patient-cell__content">
          <div className="patient-cell__title">{patientTitle}</div>
          <div className="patient-cell__email">{email}</div>
          {contact && <div className="patient-cell__contact">{contact}</div>}
        </div>
      </div>
    );
  }, [getInitials, getPresentValue, getValueOrFallback, translations.age]);

  const renderProviderCell = useCallback((rowData) => {
    const provider = rowData.attributes?.provider;
    const label = getProviderLabel(provider);

    return renderBadge(label, PROVIDER_VARIANTS[provider] || 'default');
  }, [getProviderLabel, renderBadge]);

  const renderPlatformCell = useCallback((rowData) => {
    const platform = rowData.attributes?.registration_platform;
    const label = getPlatformLabel(platform);

    let variant = 'default';
    if (platform === 'ios') variant = 'primary';
    else if (platform === 'android') variant = 'hermina';

    return renderBadge(label, variant);
  }, [getPlatformLabel, renderBadge]);

  const renderGenderCell = useCallback((rowData) => {
    const gender = rowData.attributes?.gender;
    const label = getValueOrFallback(gender);

    let variant = 'primary';
    if (gender?.toLowerCase() === 'male') variant = 'male';
    else if (gender?.toLowerCase() === 'female') variant = 'female';

    return renderBadge(label, variant);
  }, [getValueOrFallback, renderBadge]);

  const renderDateTimeCell = useCallback((value, fallback = null) => {
    if (!value) {
      return <span className="patient-text-muted">{fallback || translations.never || '-'}</span>;
    }

    const [datePart, timePart] = value.split(' - ');

    return (
      <div className="patient-datetime">
        <span className="patient-datetime__date">{datePart}</span>
        {timePart && <span className="patient-datetime__time">{timePart}</span>}
      </div>
    );
  }, [translations.never]);

  const columns = useMemo(() => [
    {
      title: translations.patient || 'Patient',
      field: 'full_name',
      sorting: false,
      render: renderPatientCell,
      cellStyle: { minWidth: 300, width: '31%' },
      headerStyle: { minWidth: 300, width: '31%' },
    },
    {
      title: translations.platform || 'Platform',
      field: 'registration_platform',
      sorting: false,
      render: renderPlatformCell,
      cellStyle: { minWidth: 115, width: '9%', whiteSpace: 'nowrap' },
      headerStyle: { minWidth: 115, width: '9%' },
    },
    {
      title: translations.provider || 'Provider',
      field: 'provider',
      sorting: false,
      render: renderProviderCell,
      cellStyle: { minWidth: 120, width: '9%', whiteSpace: 'nowrap' },
      headerStyle: { minWidth: 120, width: '9%' },
    },
    {
      title: translations.gender || 'Gender',
      field: 'gender',
      sorting: false,
      render: renderGenderCell,
      cellStyle: { minWidth: 115, width: '9%', whiteSpace: 'nowrap' },
      headerStyle: { minWidth: 115, width: '9%' },
    },
    {
      title: translations.last_sign_in || 'Last Login',
      field: 'current_sign_in_at',
      sorting: true,
      render: (rowData) => renderDateTimeCell(rowData.attributes?.current_sign_in_at, translations.never_signed_in),
      cellStyle: { minWidth: 165, width: '14%', whiteSpace: 'nowrap' },
      headerStyle: { minWidth: 165, width: '14%' },
    },
    {
      title: translations.registered_at || 'Registered At',
      field: 'created_at',
      sorting: true,
      render: (rowData) => renderDateTimeCell(rowData.attributes?.created_at),
      cellStyle: { minWidth: 165, width: '14%', whiteSpace: 'nowrap' },
      headerStyle: { minWidth: 165, width: '14%' },
    },
    {
      title: translations.last_update_at || 'Last Update At',
      field: 'updated_at',
      sorting: false,
      render: (rowData) => renderDateTimeCell(rowData.attributes?.updated_at),
      cellStyle: { minWidth: 165, width: '14%', whiteSpace: 'nowrap' },
      headerStyle: { minWidth: 165, width: '14%' },
    },
  ], [
    renderDateTimeCell,
    renderGenderCell,
    renderPatientCell,
    renderPlatformCell,
    renderProviderCell,
    translations,
  ]);

  const renderToolbar = useCallback((props) => (
    <div className="patients-toolbar">
      <div className="patients-toolbar__brand">
        <div className="patients-toolbar__title-row">
          <h1 className="patients-toolbar__title">{translations.title}</h1>
          <span className="patients-toolbar__metric">
            <span className="patients-toolbar__metric-value">{formatNumber(totalCount)}</span>
            <span className="patients-toolbar__metric-label">{translations.total_records}</span>
          </span>
        </div>
      </div>
      <div className="patients-toolbar__controls">
        <MTableToolbar
          {...props}
          classes={{
            root: 'patients-toolbar__root',
            actions: 'patients-toolbar__actions',
            spacer: 'patients-toolbar__spacer',
            searchField: 'patients-toolbar__search',
          }}
          showTitle={false}
          searchFieldAlignment="right"
          toolbarButtonAlignment="right"
        />
      </div>
    </div>
  ), [formatNumber, totalCount, translations.subtitle, translations.title, translations.total_records]);

  const handleRowClick = useCallback((event, rowData) => {
    if (rowData.attributes?.admin_account_url) {
      Turbolinks.visit(rowData.attributes.admin_account_url);
    }
  }, []);

  const handleRefresh = useCallback(() => {
    tableRef.current?.onQueryChange();
  }, []);

  return (
    <MuiThemeProvider theme={theme}>
      <div className="patients-table-wrapper">
        <MaterialTable
          title={translations.title}
          tableRef={tableRef}
          columns={columns}
          data={fetchData}
          components={{ Toolbar: renderToolbar }}
          actions={[
            {
              icon: 'refresh',
              tooltip: translations.refresh,
              isFreeAction: true,
              onClick: handleRefresh,
            },
          ]}
          options={{
            pageSize: 20,
            pageSizeOptions: [20, 50, 100],
            exportButton: false,
            sorting: true,
            search: true,
            padding: 'default',
            tableLayout: 'auto',
            grouping: false,
            debounceInterval: 500,
            draggable: false,
            thirdSortClick: false,
            emptyRowsWhenPaging: false,
            actionsColumnIndex: -1,
            headerStyle: tableStyles.headerStyle,
            cellStyle: tableStyles.cellStyle,
            rowStyle: {
              ...tableStyles.rowStyle,
              cursor: 'pointer',
            },
          }}
          localization={{
            body: { emptyDataSourceMessage: translations.empty },
            toolbar: { searchPlaceholder: translations.search_placeholder },
            pagination: {
              labelDisplayedRows: translations.pagination_label_displayed_rows || '{from}-{to} of {count}',
              labelRowsSelect: translations.pagination_label_rows_select || 'rows',
            },
          }}
          onRowClick={handleRowClick}
        />
      </div>
    </MuiThemeProvider>
  );
};

export default PatientsDataTable;
